import React, { useEffect } from "react";
import LoginPage from "./loginPage";
import { useSelector } from "react-redux";
import { Route, Switch } from "react-router";
import AuthCallback from "./authCallback";
import AuthError from "./authError";
import { SessionEnded } from "./sessionEnded";
import { AuthStep, selectAuthError, selectAuthStep } from "../../store/auth/auth.reducer";
import { userManager } from "../../../core/services/user.manager.service";
import { StoreState } from "../../store";
import { TypeSollicitation } from "../../../core/apis/monsisra/generated";
import {UtilsService} from "../../../core/services/utils.service";

/**
 * Composant utilisé comme proxy pour soit afficher l'application si l'utilisateur est authentifié, soit le rediriger vers le Portail s'il ne l'est pas.
 * @param param0 Attend en paramètre de contenu le composant à afficher si l'utilisateur est authentifié.
 */
const AuthenticationProxy = ({ children }: { children: React.ReactNode }) => {
	const authStep = useSelector((state: StoreState) => selectAuthStep(state.auth));
	const authError = useSelector((state: StoreState) => selectAuthError(state.auth));
	useEffect(() => {
		userManager.events.addAccessTokenExpiring(() => {
			onTokenExpiration();
		});

		window.addEventListener("beforeunload", async () => {
			console.log("Window is unloading, cleaning oidc lib");
			await Promise.all([userManager.removeUser(), userManager.clearStaleState(), userManager.revokeAccessToken()]);
		});
	}, []);

	const checkUser = () => {
		if (authStep === AuthStep.Disconnected) {
			//Un été final déconnecté est géré pour éviter un réauthentification
			//immédiate en cas de déconnexion explicite.
			return <SessionEnded />;
		}
		if (authError) {
			return <AuthError />;
		}
		// Lors d'une intégration en iframe on cache le loader
		return authStep !== AuthStep.Connected ? <LoginPage /> : children;
	};

	const onTokenExpiration = async () => {
		const fullPath = UtilsService.getAuthRedirectPath();
		await userManager.removeUser();
		await userManager.signinRedirect({ state: fullPath });
	};

	//La variable d'état contenant l'utilisateur est automatiquement réévaluée au niveau de ce template lorsqu'elle change dans le store,
	//ce qui doit provoquer une déconnexion immédiate en cas d'expiration de la session.
	return (
		<Switch>
			{/*URL spéciales pour gérer l'authentification*/}
			<Route path="/callback/ressource" render={() => <AuthCallback type={TypeSollicitation.DemandeAppui}/>}  />
			<Route path="/callback/ressource/download/:idDocument" render={({ match }) => <AuthCallback type={TypeSollicitation.TeleExpertise} idDocument={match.params.idDocument}/>}  />
			<Route path="/callback/expert" render={() => <AuthCallback type={TypeSollicitation.TeleExpertise}/>}  />
			<Route path="/callback/expert/download/:idDocument" render={({ match }) => <AuthCallback type={TypeSollicitation.TeleExpertise} idDocument={match.params.idDocument}/>}  />
			<Route path="/auth-error" component={AuthError} />
			{/*Pour toute autre URL authentifiée pour lesquelles la session utilisateur est vérifiée*/}
			<Route>{checkUser()}</Route>
		</Switch>
	);
};

export default AuthenticationProxy;
